import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Row, Col, Button, Spin, Alert } from 'antd';
import * as Yup from 'yup';
import { 
  FormikInputField, 
  FormikTextAreaField, 
  FormikSelectField, 
  FormikNumberField, 
  FormikDateField, 
  FormikDateTimeField,
  FormikFileField,
  FormikTableField,
  FormikSQLSelectField,
  FormikBooleanField,
  FormikYearBasedCostField,
  FormikHisselerField,
  FormikCheckboxField,
  FormikKonumField
} from './FormikFields';

const fieldComponentMap = {
  input: FormikInputField,
  textarea: FormikTextAreaField,
  select: FormikSelectField,
  number: FormikNumberField,
  date: FormikDateField,
  datetime: FormikDateTimeField,
  file: FormikFileField,
  table: FormikTableField,
  sqlselect: FormikSQLSelectField,
  Boolean: FormikBooleanField,
  yearbasedcost: FormikYearBasedCostField,
  hisseler: FormikHisselerField,
  checkbox: FormikCheckboxField,
  konum: FormikKonumField,
};

const FormikDynamicForm = forwardRef(({
  schema,
  initialValues = {},
  onSubmit,
  onValuesChange,
  loading = false,
  submitButtonText = "Kaydet",
  darkMode = false,
  formId = null,
  uniqueValue = null
}, ref) => {
  const [validationSchema, setValidationSchema] = useState(null);
  const formikRef = useRef(null);

  useImperativeHandle(ref, () => ({
    setFieldValue: (name, value) => {
      if (formikRef.current && typeof formikRef.current.setFieldValue === 'function') {
        formikRef.current.setFieldValue(name, value);
      }
    },
    getFormikRef: () => formikRef.current
  }));

  useEffect(() => {
    if (!schema?.fields) return;

    const yupSchema = {};
    
    schema.fields.forEach(field => {
      if (!field.validation) return;

      let fieldSchema = Yup.string();
      
      if (field.type === 'number') {
        fieldSchema = Yup.number();
      } else if (field.type === 'date') {
        fieldSchema = Yup.date();
      }

      if (field.validation.required) {
        fieldSchema = fieldSchema.required(`${field.label} gerekli`);
      }

      if (field.validation.min !== undefined) {
        if (field.type === 'number') {
          fieldSchema = fieldSchema.min(field.validation.min, `Minimum ${field.validation.min} olmalı`);
        } else {
          fieldSchema = fieldSchema.min(field.validation.min, `En az ${field.validation.min} karakter olmalı`);
        }
      }

      if (field.validation.max !== undefined) {
        if (field.type === 'number') {
          fieldSchema = fieldSchema.max(field.validation.max, `Maksimum ${field.validation.max} olmalı`);
        } else {
          fieldSchema = fieldSchema.max(field.validation.max, `En fazla ${field.validation.max} karakter olmalı`);
        }
      }

      yupSchema[field.name] = fieldSchema;
    });

    setValidationSchema(Yup.object().shape(yupSchema));
  }, [schema]);

  if (!schema?.fields) {
    return <Alert message="Form şeması bulunamadı" type="error" />;
  }

  const processValue = (value) => {
    if (typeof value === 'string') {
      if (value === '{{today}}') {
        return new Date().toISOString().split('T')[0]; 
      }
    }
    return value;
  };

  const getDefaultValue = (field) => {
    const initialValue = processValue(initialValues[field.name]);
    const defaultValue = processValue(field.defaultValue);
    const value = initialValue || defaultValue;
    
    if (field.type === 1) {
      return value || 0;
    }
    if (field.type === 5 || field.type === '5') {
      if (value !== undefined && value !== null && value !== '') {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(numValue) ? '' : numValue;
      }
      return ''; 
    }
    
    if (field.name === 'kullanici_surucu') {
      if (value === 0 || value === '0') {
        return 0;
      }
      return value || 0;
    }
    
    if (typeof value === 'string' && value.includes(':Seçiniz')) {
      return '';
    }
    
    if (field.type === 'select' && typeof value === 'string') {
      if (value === '0' || value.startsWith('0:')) {
        return '';
      }
    }
    
    return value || '';
  };

  const formInitialValues = {};
  schema.fields.forEach(field => {
    formInitialValues[field.name] = getDefaultValue(field);
  });

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      await onSubmit(values, { setSubmitting, setStatus });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({ error: 'Form gönderilirken hata oluştu' });
    }
  };

  const shouldShowField = (field, values) => {
    if (formId === 258 || formId === '258') {
      const kurumFields = [
        'kurum_unvan', 
        'kurum_tel', 
        'kurum_mail', 
        'kurum_adres_il', 
        'kurum_adres_ilce', 
        'kurum_adres_mah', 
        'kurum_adres_detay',
        'vergi_no',
        'vergi_dairesi'
      ];
      if (kurumFields.includes(field.name)) {
        return values.yetkili_tipi === 1 || values.yetkili_tipi === '1';
      }
    }

    if (field.name && field.name.startsWith('teklif[') && field.name.endsWith(']')) {
      const attr = field.attr || '';
      if (attr.includes("teklifHide='hideable'") || attr.includes('teklifHide="hideable"')) {
        const match = field.name.match(/teklif\[(\d+)\]/);
        if (match) {
          const expectedKey = match[1];
          const bankaPassValue = values['banka-pass'];
          
          if (!bankaPassValue || bankaPassValue === '' || bankaPassValue === null || bankaPassValue === undefined) {
            return false;
          }
          
          let selectedKey;
          if (Array.isArray(bankaPassValue)) {
            selectedKey = bankaPassValue.length > 0 ? String(bankaPassValue[0]) : null;
          } else {
            selectedKey = String(bankaPassValue);
          }
          
          if (!selectedKey) {
            return false;
          }
          
          const expectedKeyStr = String(expectedKey);
          return selectedKey === expectedKeyStr;
        }
      }
    }
  
    return true;
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, isSubmitting, status, setFieldValue }) => {
        return (
          <Form>
            {status?.error && (
              <Alert 
                message={status.error} 
                type="error" 
                style={{ marginBottom: 16 }} 
              />
            )}
            
            <Row gutter={[16, 16]}>
              {schema.fields
                .sort((a, b) => a.order - b.order)
                .map((field) => {
                  if (field.type === 5) {
                    return null;
                  }
                  
                  if (field.name === 'arac_surucu') {
                    return null;
                  }
                  
                  const isFieldVisible = shouldShowField(field, values);
                  const isTeklifField = field.name && field.name.startsWith('teklif[') && field.name.endsWith(']');
                  const shouldRenderHidden = (formId === 199 || formId === '199') && isTeklifField && !isFieldVisible;
                  
                  if (!isFieldVisible && !shouldRenderHidden) {
                    return null;
                  }
                  
                  let fieldType = field.type;
                  
                  if (typeof field.type === 'string') {
                    if (fieldComponentMap[field.type]) {
                      fieldType = field.type;
                    } else if (field.type === '3') {
                      fieldType = 'input';
                    } else if (field.type === '1') {
                      fieldType = 'input'; 
                    }
                  } else if (typeof field.type === 'number') {
                    const typeNum = Number(field.type);
                    if (typeNum === 3) {
                      fieldType = 'input';
                    } else if (typeNum === 1) {
                      fieldType = 'input'; 
                    } else if (typeNum === 11) {
                      fieldType = 'table';
                    }
                  } else if (field.type === 'Boolean') {
                    fieldType = 'Boolean';
                  }
                  
                  const FieldComponent = fieldComponentMap[fieldType];
                  if (!FieldComponent) {
                    console.warn(`Unknown field type: ${field.type} (typeof: ${typeof field.type}, mapped to: ${fieldType}) for field ${field.name}`);
                    return null;
                  }

                  return (
                    <Col 
                      key={field.name} 
                      span={field.width || 12}
                      style={shouldRenderHidden ? { display: 'none' } : {}}
                    >
                      <Field
                        name={field.name}
                      >
                        {(fieldProps) => (
                          <FieldComponent
                            {...fieldProps}
                            fieldConfig={field}
                            setFieldValue={setFieldValue}
                            darkMode={darkMode}
                            formId={formId}
                            uniqueValue={uniqueValue}
                            // Gizli (tip 7) alanlar şemaya girmediği için extraData
                            // (ör. kabin taşımada `kabin`) Formik values'ta yok.
                            {...(fieldType === 'konum' && { extraValues: initialValues })}
                          />
                        )}
                      </Field>
                      <ErrorMessage 
                        name={field.name} 
                        component="div" 
                        className="error-message"
                        style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}
                      />
                    </Col>
                  );
                })}
            </Row>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting || loading}
                size="large"
                style={{
                  backgroundColor: darkMode ? '#1890ff' : '#1890ff',
                  borderColor: darkMode ? '#1890ff' : '#1890ff'
                }}
              >
                {submitButtonText || schema.submitText || 'Kaydet'}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
});

FormikDynamicForm.displayName = 'FormikDynamicForm';

export default FormikDynamicForm;